import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CircularProgress,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Switch,
  FormControlLabel,
  Tooltip as MuiTooltip,
} from '@mui/material';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftButton from 'components/SoftButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';

import { getAllTenants, activateTenant, deactivateTenant } from 'services/tenantService';

// Mirrors the archive statuses in server/TenantManagementService/archive_worker.py.
const ARCHIVE_IN_PROGRESS = ['PENDING', 'ARCHIVING', 'VERIFYING', 'DELETING', 'RESTORING'];

// A tenant is archived once its data has been exported and purged. A FAILED
// status with the archive still in place means a restore did not finish, so the
// data is still archived — same treatment as the tenant dashboard's Danger Zone.
const isArchivedTenant = (t) =>
  t.archiveStatus === 'ARCHIVED' || (t.archiveStatus === 'FAILED' && !!t.archiveKey);

const archiveLabel = (t) => {
  if (t.archiveStatus === 'ARCHIVED') return { label: 'Archived', color: 'default' };
  if (t.archiveStatus === 'FAILED') {
    return t.archiveKey
      ? { label: 'Restore failed', color: 'error' }
      : { label: 'Archive failed', color: 'error' };
  }
  if (ARCHIVE_IN_PROGRESS.includes(t.archiveStatus)) {
    return { label: t.archiveStatus === 'RESTORING' ? 'Restoring' : 'Archiving', color: 'warning' };
  }
  return null;
};

const formatArchivedAt = (timestamp) => {
  if (!timestamp) return '';
  return new Date(Number(timestamp) * 1000).toLocaleDateString();
};

function TenantList() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, tenant: null });
  const [showArchived, setShowArchived] = useState(false);

  const handleStatusClick = (e, tenant) => {
    e.stopPropagation();
    setConfirmDialog({ open: true, tenant });
  };

  const handleStatusConfirm = async () => {
    const { tenant } = confirmDialog;
    setConfirmDialog({ open: false, tenant: null });
    const tenantId = tenant.tenantId || tenant.tenant_id;
    try {
      if (tenant.isActive) {
        await deactivateTenant(tenantId);
        setSnackbar({ open: true, message: 'Tenant deactivated', severity: 'success' });
      } else {
        await activateTenant(tenantId);
        setSnackbar({ open: true, message: 'Tenant activated', severity: 'success' });
      }
      await loadTenants();
    } catch (err) {
      console.error('Error updating tenant status:', err);
      setSnackbar({ open: true, message: 'Failed to update tenant status', severity: 'error' });
    }
  };

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const data = await getAllTenants();
      const sorted = (Array.isArray(data) ? data : []).sort((a, b) => {
        const aArchived = isArchivedTenant(a);
        const bArchived = isArchivedTenant(b);
        if (aArchived !== bArchived) return aArchived ? 1 : -1;
        if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
        return (a.businessName || '').localeCompare(b.businessName || '');
      });
      setTenants(sorted);
    } catch (err) {
      console.error('Error loading tenants:', err);
      setSnackbar({ open: true, message: 'Failed to load tenants', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const archivedCount = tenants.filter(isArchivedTenant).length;
  const visibleTenants = showArchived ? tenants : tenants.filter((t) => !isArchivedTenant(t));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Card>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
              <SoftTypography variant="h6">Tenants</SoftTypography>
              <SoftBox display="flex" alignItems="center">
                <FormControlLabel
                  control={
                    <Switch
                      checked={showArchived}
                      onChange={(e) => setShowArchived(e.target.checked)}
                    />
                  }
                  label={
                    <SoftTypography variant="button" color="text">
                      {`Show archived${archivedCount ? ` (${archivedCount})` : ''}`}
                    </SoftTypography>
                  }
                  sx={{ mr: 2 }}
                />
                <SoftButton
                  variant="gradient"
                  color="info"
                  onClick={() => navigate('/tenants/create')}
                >
                  Add Tenant
                </SoftButton>
              </SoftBox>
            </SoftBox>
            <SoftBox>
              {loading ? (
                <SoftBox display="flex" justifyContent="center" alignItems="center" p={3}>
                  <CircularProgress />
                </SoftBox>
              ) : visibleTenants.length === 0 ? (
                <SoftBox p={3}>
                  <Alert severity="info">
                    {tenants.length === 0
                      ? 'No tenants found.'
                      : `No unarchived tenants. Turn on "Show archived" to see ${archivedCount === 1 ? 'the archived tenant' : `all ${archivedCount} archived tenants`}.`}
                  </Alert>
                </SoftBox>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead sx={{ display: "table-header-group" }}>
                      <TableRow>
                        <TableCell>Tenant ID</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Address</TableCell>
                        <TableCell>Short Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Plan</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Archived</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleTenants.map((tenant) => (
                        <TableRow
                          key={tenant.tenantId || tenant.tenant_id}
                          hover
                          sx={{
                            cursor: 'pointer',
                            ...(isArchivedTenant(tenant) ? { opacity: 0.6 } : {}),
                          }}
                          onClick={() => navigate(`/tenants/dashboard/${tenant.tenantId || tenant.tenant_id}`)}
                        >
                          <TableCell>
                            <SoftTypography variant="caption" fontWeight="medium">
                              {tenant.tenantId}
                            </SoftTypography>
                          </TableCell>
                          <TableCell>
                            <SoftTypography variant="caption">
                              {tenant.businessName}
                            </SoftTypography>
                          </TableCell>
                          <TableCell>
                            <SoftTypography variant="caption">
                              {tenant.tenantPhone}
                            </SoftTypography>
                          </TableCell>
                          <TableCell>
                            <SoftTypography variant="caption">
                              {tenant.tenantAddress}
                            </SoftTypography>
                          </TableCell>
                          <TableCell>
                            <SoftTypography variant="caption">
                              {tenant.tenantName}
                            </SoftTypography>
                          </TableCell>
                          <TableCell>
                            <SoftTypography variant="caption">
                              {tenant.tenantEmail}
                            </SoftTypography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={tenant.tenantTier}
                              size="small"
                              color={
                                tenant.tenantTier === 'Platinum' ? 'primary' :
                                tenant.tenantTier === 'Premium' ? 'secondary' :
                                'default'
                              }
                            />
                          </TableCell>
                          <TableCell>
                            {/* Activating an archived tenant would bring back a
                                shell whose data has been purged — restore first. */}
                            <Chip
                              label={tenant.isActive ? 'Active' : 'Inactive'}
                              size="small"
                              color={tenant.isActive ? 'success' : 'default'}
                              onClick={
                                isArchivedTenant(tenant)
                                  ? undefined
                                  : (e) => handleStatusClick(e, tenant)
                              }
                              clickable={!isArchivedTenant(tenant)}
                            />
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const badge = archiveLabel(tenant);
                              if (!badge) {
                                return (
                                  <SoftTypography variant="caption" color="text">
                                    —
                                  </SoftTypography>
                                );
                              }
                              const chip = (
                                <Chip label={badge.label} size="small" color={badge.color} />
                              );
                              const detail = tenant.archiveError
                                || (tenant.archivedAt
                                  ? `Archived ${formatArchivedAt(tenant.archivedAt)}`
                                  : '');
                              return detail ? (
                                <MuiTooltip title={detail}>
                                  <span>{chip}</span>
                                </MuiTooltip>
                              ) : chip;
                            })()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </SoftBox>
          </Card>
        </SoftBox>
      </SoftBox>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, tenant: null })}
      >
        <DialogTitle>
          {confirmDialog.tenant?.isActive ? 'Deactivate' : 'Activate'} Tenant
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {confirmDialog.tenant?.isActive ? 'deactivate' : 'activate'}{' '}
            <strong>{confirmDialog.tenant?.businessName}</strong>?
            {confirmDialog.tenant?.isActive &&
              ' This will disable all users for this tenant.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, tenant: null })}>Cancel</Button>
          <Button
            onClick={handleStatusConfirm}
            color={confirmDialog.tenant?.isActive ? 'error' : 'success'}
            variant="contained"
          >
            {confirmDialog.tenant?.isActive ? 'Deactivate' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default TenantList;
